import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Icon from 'components/AppIcon';
import SEOHelmet from 'components/SEOHelmet';

import HeroSection from './components/HeroSection';
import PersonalityWorlds from './components/PersonalityWorlds';
import FeaturedProjects from './components/FeaturedProjects';
import SocialFeed from './components/SocialFeed';
import Testimonials from './components/Testimonials';
import WhatIUse from './components/WhatIUse';
import TechStack from './components/TechStack';
import ContactSection from './components/ContactSection';
import BookingSection from './components/BookingSection';
import Image from 'components/AppImage';

const LandingPage = () => {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <>
      <SEOHelmet
        title="Nikunj Navdiya - Full Stack Developer & Web3 Engineer | Portfolio"
        description="Explore the multi-dimensional portfolio of Nikunj Navdiya - Software Developer, Web3 Engineer, and Community Builder. Discover my journey through three professional worlds: Community Leadership, Blockchain Development, and Full-Stack Engineering."
        keywords="Nikunj Navdiya, Full Stack Developer, Web3 Developer, Software Engineer, React Developer, Node.js, Blockchain, Community Builder, Portfolio, India, JavaScript, TypeScript"
        canonical="/"
        world="landing"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Nikunj Navdiya",
          "url": "https://nikunj-navdiya.vercel.app",
          "image": "https://nikunj-navdiya.vercel.app/avatar.jpg",
          "sameAs": [
            "https://twitter.com/nikunjnavdiya",
            "https://linkedin.com/in/nikunjnavdiya",
            "https://github.com/nikunjnavdiya"
          ],
          "jobTitle": "Full Stack Developer & Web3 Engineer",
          "worksFor": {
            "@type": "Organization",
            "name": "Freelance"
          },
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "IN"
          },
          "knowsAbout": [
            "Software Development",
            "Web3 Development", 
            "Blockchain Technology",
            "Community Building",
            "React.js",
            "Node.js",
            "JavaScript",
            "TypeScript",
            "DeFi",
            "Smart Contracts"
          ],
          "description": "Multi-dimensional developer specializing in full-stack development, blockchain technology, and community building with 3+ years of experience."
        }}
      />
      <div className="min-h-screen bg-background">
        {/* Main Content */}
        <main className="pt-16 md:pt-20">
        {/* Hero Section */}
        <HeroSection />

        {/* Personality Worlds */}
        <PersonalityWorlds />

        {/* Featured Projects */}
        <FeaturedProjects />

        {/* Booking Section */}
        <BookingSection />

        {/* Social Feed */}
        <SocialFeed />

        {/* Testimonials */}
        <Testimonials />

        {/* What I Use */}
        <WhatIUse />

        {/* Tech Stack */}
        {/* <TechStack /> */}

        {/* Contact Section */}
        <ContactSection />
      </main>

      {/* Footer */}
      <footer className="bg-primary text-surface py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-accent to-warning rounded-lg flex items-center justify-center">
                  <span className="text-surface font-heading font-bold text-lg">N</span>
                </div>
                <h3 className="font-heading font-bold text-xl">Nikunj</h3>
              </div>
              <p className="font-body text-surface/80 text-sm">
                Multi-dimensional developer crafting experiences across communities, Web3, and software development.
              </p>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold text-lg mb-4">Quick Links</h4>
              <div className="space-y-2">
                {[
                  { label: 'Community Guy', path: '/community-guy-world' },
                  { label: 'Web3 Developer', path: '/web3-developer-world' },
                  { label: 'Software Developer', path: '/software-developer-world' },
                  { label: 'Book a Call', path: '#book-call' }
                ].map((link) => (
                  <button
                    key={link.path}
                    onClick={() => {
                      if (link.path.startsWith('#')) {
                        document.querySelector(link.path).scrollIntoView({ behavior: 'smooth' });
                      } else {
                        navigate(link.path);
                      }
                    }}
                    className="block font-body text-surface/80 hover:text-surface transition-colors duration-200 text-sm"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold text-lg mb-4">Connect</h4>
              <div className="flex space-x-4">
                {[
                  {  label: 'X', icon: <Image src="/icons/twitter.svg" alt="Twitter" width={16} height={16} /> , link: 'https://x.com/navdiya_nikunj' },
                  {  label: 'LinkedIn', icon: <Icon name="Linkedin" size={16} /> , link: 'https://www.linkedin.com/in/nikunj-navdiya-024543235/' },
                  {  label: 'GitHub', icon: <Icon name="Github" size={16} /> , link: 'https://github.com/navdiya-nikunj' },
                ].map((social) => (
                  <button
                    onClick={() => window.open(social.link, '_blank')}
                    key={social.label}
                    className="w-10 h-10 bg-surface/10 rounded-lg flex items-center justify-center hover:bg-surface/20 transition-colors duration-200"
                    aria-label={social.label}
                  >
                    {social.icon}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="border-t border-surface/20 mt-8 pt-8 text-center">
            <p className="font-caption text-surface/60 text-sm">
              © {new Date().getFullYear()} Portfolio. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default LandingPage;